import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Delete as DeleteIcon, Transcribe as TranscribeIcon, CompareArrows, PostAdd } from '@mui/icons-material';
import { apiService } from '../services/api';
import FileUpload from '../components/upload/FileUpload';
import TranscriptionCard from '../components/transcription/TranscriptionCard';
import ContextForm from '../components/context/ContextForm';
import AudioPlayer from '../components/audio/AudioPlayer';
// import CreateReferenceTranscriptDialog from '../components/transcripts/CreateReferenceTranscriptDialog';

function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [transcriptions, setTranscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transcribing, setTranscribing] = useState({});
  const [availableProviders, setAvailableProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o-transcribe');
  const [createReferenceDialogOpen, setCreateReferenceDialogOpen] = useState(false);

  useEffect(() => {
    loadProjectData();
    loadAvailableProviders();
  }, [projectId]);

  // Load models when provider changes
  useEffect(() => {
    if (selectedProvider) {
      loadAvailableModels();
    }
  }, [selectedProvider]);

  // Smart default model selection based on project context
  useEffect(() => {
    if (project && availableModels.length > 0) {
      // If project has speaker names, use model with speaker separation
      if (project.interviewer_name || project.interviewee_name) {
        const excellentModel = availableModels.find(m => m.speaker_separation_capability === 'excellent');
        if (excellentModel && selectedModel !== excellentModel.id) {
          setSelectedModel(excellentModel.id);
        }
      }
    }
  }, [project, availableModels]);

  const loadAvailableProviders = async () => {
    try {
      // For now, we know OpenAI, Assembly AI, and Google Gemini are available
      // In the future, we could make an API call to get this dynamically
      setAvailableProviders([
        {
          id: 'openai',
          name: 'OpenAI',
          description: 'OpenAI Transcriptions API with GPT-4 models'
        },
        {
          id: 'assembly_ai',
          name: 'Assembly AI',
          description: 'Assembly AI speech-to-text with automatic diarization'
        },
        {
          id: 'google_gemini',
          name: 'Google Gemini',
          description: 'Google Gemini multimodal AI with advanced audio understanding'
        }
      ]);
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  };

  const loadAvailableModels = async () => {
    try {
      const result = await apiService.getProviderModels(selectedProvider);
      setAvailableModels(result.models || []);

      // Set default model for the provider if current selection is not available
      if (result.models && result.models.length > 0) {
        const modelIds = result.models.map(m => m.id);
        if (!modelIds.includes(selectedModel)) {
          // Use first model with excellent speaker separation if available
          const excellentModel = result.models.find(m => m.speaker_separation_capability === 'excellent');
          setSelectedModel(excellentModel?.id || result.models[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load models:', err);
      // Set default models based on provider
      if (selectedProvider === 'openai') {
        setAvailableModels([
          {
            id: 'gpt-4o-transcribe',
            description: 'High-accuracy transcription with context support',
            supports_diarization: false,
            supports_prompt: true,
            speaker_separation_capability: 'excellent',
            supports_context: true,
            best_for: 'interviews with known participants'
          },
          {
            id: 'gpt-4o-transcribe-diarize',
            description: 'High-accuracy with automatic speaker diarization',
            supports_diarization: true,
            supports_prompt: false,
            speaker_separation_capability: 'automatic',
            supports_context: false,
            best_for: 'conversations without prior context'
          },
          {
            id: 'gpt-4o-mini-transcribe',
            description: 'Lower-cost, faster transcription',
            supports_diarization: false,
            supports_prompt: true,
            speaker_separation_capability: 'none',
            supports_context: true,
            best_for: 'fast, low-cost transcription without speaker separation'
          },
        ]);
      } else if (selectedProvider === 'assembly_ai') {
        setAvailableModels([
          {
            id: 'assemblyai-core',
            description: 'High-quality transcription with automatic speaker diarization',
            supports_diarization: true,
            supports_prompt: false,
            speaker_separation_capability: 'automatic',
            supports_context: false,
            best_for: 'high-quality transcription with built-in speaker identification'
          },
        ]);
        setSelectedModel('assemblyai-core');
      } else if (selectedProvider === 'google_gemini') {
        setAvailableModels([
          {
            id: 'gemini-2.5-flash',
            description: 'Gemini 2.5 Flash - improved speed and accuracy',
            supports_diarization: true,
            supports_prompt: true,
            speaker_separation_capability: 'automatic',
            supports_context: true,
            best_for: 'latest stable Flash model, balanced performance'
          },
          {
            id: 'gemini-2.5-flash-lite',
            description: 'Gemini 2.5 Flash Lite - fastest and most cost-effective',
            supports_diarization: true,
            supports_prompt: true,
            speaker_separation_capability: 'automatic',
            supports_context: true,
            best_for: 'quick transcriptions, lower cost'
          },
          {
            id: 'gemini-2.5-pro',
            description: 'Gemini 2.5 Pro - highest quality with large context',
            supports_diarization: true,
            supports_prompt: true,
            speaker_separation_capability: 'automatic',
            supports_context: true,
            best_for: 'highest accuracy, complex audio, long files'
          },
          {
            id: 'gemini-3-pro-preview',
            description: 'Gemini 3 Pro Preview - experimental next-generation model',
            supports_diarization: true,
            supports_prompt: true,
            speaker_separation_capability: 'automatic',
            supports_context: true,
            best_for: 'cutting-edge features (experimental)'
          },
          {
            id: 'gemini-2.0-flash',
            description: 'Gemini 2.0 Flash - stable version with advanced audio understanding',
            supports_diarization: true,
            supports_prompt: true,
            speaker_separation_capability: 'automatic',
            supports_context: true,
            best_for: 'production-ready, reliable transcription'
          },
          {
            id: 'gemini-2.0-flash-exp',
            description: 'Gemini 2.0 Flash Experimental - testing latest features',
            supports_diarization: true,
            supports_prompt: true,
            speaker_separation_capability: 'automatic',
            supports_context: true,
            best_for: 'experimental features, early access'
          },
        ]);
        setSelectedModel('gemini-2.5-flash');
      }
    }
  };

  const loadProjectData = async () => {
    try {
      setLoading(true);
      const [projectData, filesData, transcriptionsData] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getProjectFiles(projectId),
        apiService.getProjectTranscriptions(projectId),
      ]);
      setProject(projectData);
      setFiles(filesData);
      setTranscriptions(transcriptionsData);
      setError(null);
    } catch (err) {
      setError('Failed to load project data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = (fileMetadata) => {
    // Refresh files list
    loadProjectData();
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await apiService.deleteFile(fileId);
      setError(null);
      // Refresh files list
      await loadProjectData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete file';
      setError(`Failed to delete file: ${errorMessage}`);
      console.error('Delete file error:', err);
    }
  };

  const handleStartTranscription = async (fileId) => {
    try {
      setTranscribing(prev => ({ ...prev, [fileId]: true }));
      const result = await apiService.startTranscription(projectId, fileId, selectedProvider, selectedModel);

      // Refresh transcriptions list
      loadProjectData();
    } catch (err) {
      setError('Failed to start transcription');
      console.error(err);
    } finally {
      setTranscribing(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDeleteTranscription = async (transcriptId) => {
    if (!window.confirm('Are you sure you want to delete this transcription?')) {
      return;
    }

    try {
      await apiService.deleteTranscription(transcriptId);
      setError(null);
      await loadProjectData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete transcription';
      setError(`Failed to delete transcription: ${errorMessage}`);
      console.error('Delete transcription error:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project && !loading) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Project not found
      </Alert>
    );
  }

  const handleContextUpdated = () => {
    // Refresh project data to show updated context
    loadProjectData();
  };

  const handleReferenceTranscriptCreated = (newTranscript) => {
    // Refresh project data to show new reference transcript
    loadProjectData();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {project.name}
      </Typography>

      {/* Show error message but don't hide the page */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Project Information */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Project Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {project.domain && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Domain
                </Typography>
                <Box>
                  <Chip label={project.domain} size="small" />
                </Box>
              </Box>
            )}

            {project.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">{project.description}</Typography>
              </Box>
            )}

            {project.interviewer_name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Interviewer
                </Typography>
                <Typography variant="body2">{project.interviewer_name}</Typography>
              </Box>
            )}

            {project.interviewee_name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Interviewee
                </Typography>
                <Typography variant="body2">{project.interviewee_name}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Context Form */}
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 3 }}>
            <ContextForm projectId={projectId} onContextUpdated={handleContextUpdated} />
          </Box>
        </Grid>

        {/* File Upload and List */}
        <Grid item xs={12}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload Audio File
            </Typography>
            <FileUpload projectId={projectId} onUploadComplete={handleUploadComplete} />
          </Box>

          <Paper sx={{ p: 2 }}>
            {/* Provider Selection */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="provider-select-label">Transcription Provider</InputLabel>
                <Select
                  labelId="provider-select-label"
                  id="provider-select"
                  value={selectedProvider}
                  label="Transcription Provider"
                  onChange={(e) => setSelectedProvider(e.target.value)}
                >
                  {availableProviders.map((provider) => (
                    <MenuItem key={provider.id} value={provider.id}>
                      <Box>
                        <Typography variant="body2">{provider.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {provider.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Model Selection */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="model-select-label">Transcription Model</InputLabel>
                <Select
                  labelId="model-select-label"
                  id="model-select"
                  value={selectedModel}
                  label="Transcription Model"
                  onChange={(e) => setSelectedModel(e.target.value)}
                >
                  {availableModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          {model.id}
                        </Box>
                        {/* Speaker separation capability badge */}
                        {model.speaker_separation_capability === 'excellent' && (
                          <Chip label="Speaker Labels" size="small" color="success" />
                        )}
                        {model.speaker_separation_capability === 'automatic' && (
                          <Chip label="Auto Diarization" size="small" color="info" />
                        )}
                        {/* Context support badge */}
                        {model.supports_context && (
                          <Chip label="Context" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Selected model info */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {availableModels.find(m => m.id === selectedModel)?.description || 'Select a model for transcription'}
                </Typography>
                {availableModels.find(m => m.id === selectedModel)?.best_for && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    Best for: {availableModels.find(m => m.id === selectedModel)?.best_for}
                  </Typography>
                )}
              </Box>

              {/* Warning for models without speaker separation when project has speaker context */}
              {availableModels.find(m => m.id === selectedModel)?.speaker_separation_capability === 'none' &&
               (project.interviewer_name || project.interviewee_name) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Note:</strong> This model does not support speaker separation. While it will use your
                  vocabulary and context for accuracy, it won't label speakers in the transcript. Consider using{' '}
                  <strong>gpt-4o-transcribe</strong> to identify speakers by name.
                </Alert>
              )}

              {/* Info for automatic diarization models */}
              {availableModels.find(m => m.id === selectedModel && m.supports_prompt === false) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Automatic Speaker Diarization:</strong> This model automatically detects and labels
                  speakers but does not support custom context (names, bio, vocabulary). Speakers will be
                  labeled as "Speaker 1", "Speaker 2", etc. Use <strong>gpt-4o-transcribe</strong> if you want
                  speakers identified by name.
                </Alert>
              )}

              {/* Success message for optimal model choice */}
              {availableModels.find(m => m.id === selectedModel)?.speaker_separation_capability === 'excellent' &&
               (project.interviewer_name || project.interviewee_name) && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <strong>Optimal Configuration:</strong> This model will label speakers using the names you
                  provided ({project.interviewer_name && project.interviewer_name}
                  {project.interviewer_name && project.interviewee_name && ' and '}
                  {project.interviewee_name && project.interviewee_name}) and use your context for accuracy.
                </Alert>
              )}
            </Box>

            {files.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Uploaded Files
                </Typography>
                <List>
                {files.map((file) => (
                  <ListItem
                    key={file.id}
                    secondaryAction={
                      <Box>
                        {file.status === 'uploaded' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<TranscribeIcon />}
                            onClick={() => handleStartTranscription(file.id)}
                            disabled={transcribing[file.id]}
                            sx={{ mr: 1 }}
                          >
                            {transcribing[file.id] ? 'Starting...' : 'Transcribe'}
                          </Button>
                        )}
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteFile(file.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={file.original_filename}
                      secondary={
                        <Box component="span">
                          <Box component="span" sx={{ display: 'block' }}>
                            Size: {formatFileSize(file.file_size)} | Duration:{' '}
                            {formatDuration(file.duration)}
                          </Box>
                          <Box component="span" sx={{ display: 'block' }}>
                            {file.sample_rate} Hz | {file.channels} channel
                            {file.channels > 1 ? 's' : ''}
                          </Box>
                          <Chip
                            label={file.status}
                            size="small"
                            color={file.status === 'uploaded' ? 'success' : 'default'}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                </List>
              </>
            )}

            {/* Audio Player Section */}
            {files.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <AudioPlayer audioFiles={files} />
              </Box>
            )}

            {/* Transcriptions Section */}
            {transcriptions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Transcriptions
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {files.length > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<PostAdd />}
                        onClick={() => setCreateReferenceDialogOpen(true)}
                        size="small"
                      >
                        Create Reference Transcript
                      </Button>
                    )}
                    {files.length > 0 && transcriptions.filter(t => t.audio_file_id === files[0].id).length >= 2 && (
                      <Button
                        variant="outlined"
                        startIcon={<CompareArrows />}
                        onClick={() => navigate(`/compare/${files[0].id}`)}
                        size="small"
                      >
                        Compare Transcripts
                      </Button>
                    )}
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  {transcriptions.map((transcription, index) => (
                    <Grid item xs={12} key={transcription.id}>
                      <TranscriptionCard
                        transcription={transcription}
                        transcriptionNumber={index + 1}
                        onDelete={handleDeleteTranscription}
                        onTranscriptionUpdate={loadProjectData}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create Reference Transcript Dialog - Temporarily disabled */}
      {/* {files.length > 0 && (
        <CreateReferenceTranscriptDialog
          open={createReferenceDialogOpen}
          onClose={() => setCreateReferenceDialogOpen(false)}
          audioFileId={files[0].id}
          onTranscriptCreated={handleReferenceTranscriptCreated}
        />
      )} */}
    </Box>
  );
}

export default ProjectDetailPage;
